import React, { useState, useEffect } from "react";
import { Box, useTheme,  AppBar, Toolbar, Typography, Link} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  ArrowBack,
} from "@mui/icons-material";
import Sidebar from "./Documentation/Sidebar";
import MarkdownContent from "./Documentation/MarkdownContent";
import { Link as RouterLink } from "react-router-dom";
import { documentationStructure } from "../constants/documentation";


const userDetails = localStorage.getItem("userDetail");
const user = JSON.parse(userDetails);

const DocumentationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
  background: "linear-gradient(180deg, #FFF5F3 0%, #FFFFFF 100%)",
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: "linear-gradient(90deg, #FF5336 0%, #FF7A36 100%)",
  color: "#ffffff",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  borderBottom: "none",
  zIndex: theme.zIndex.drawer + 1,
  height: "64px",
}));

const Logo = styled('img')({
  height: '40px',
  width: 'auto',
  objectFit: 'contain',
});

const MainContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flex: 1,
  marginTop: 0,
  height: "calc(100vh - 64px)",
  position: "relative",
  backgroundColor: "#f8fafc",
}));

const SidebarContainer = styled(Box)(({ theme }) => ({
  width: 300,
  flexShrink: 0,
  position: "fixed",
  left: 0,
  top: 64,
  height: "calc(100vh - 64px)",
  backgroundColor: "#ffffff",
  borderRight: "1px solid #e2e6f3",
  overflowY: "auto",
  boxShadow: "2px 0 4px rgba(0,0,0,0.02)",
}));

const Content = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(4),
  overflow: "auto",
  backgroundColor: "#ffffff",
  margin: theme.spacing(3),
  marginLeft: 316,
  marginRight: theme.spacing(3),
  borderRadius: "12px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  height: "calc(100vh - 104px)",
  position: "relative",
  "&::-webkit-scrollbar": {
    width: "8px",
    height: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#FFF5F3",
    borderRadius: "4px",
    margin: "4px 0",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#FF5336",
    borderRadius: "4px",
    border: "2px solid #FFF5F3",
    "&:hover": {
      background: "#FF7A36",
    },
  },
  scrollbarWidth: "thin",
  scrollbarColor: "#FF5336 #FFF5F3",
  "& > *": {
    paddingRight: "8px",
  },
  "& > div": {
    maxWidth: "1200px",
    margin: "0 auto",
  },
  "& h1": {
    fontSize: "2.5rem",
    fontWeight: 700,
    color: "#1a1f36",
    marginBottom: "1.5rem",
    letterSpacing: "-0.02em",
    borderBottom: "2px solid #FF5336",
    paddingBottom: "0.5rem",
  },
  "& h2": {
    fontSize: "1.875rem",
    fontWeight: 600,
    color: "#1a1f36",
    marginTop: "2.5rem",
    marginBottom: "1rem",
    letterSpacing: "-0.01em",
    "&::before": {
      content: '""',
      display: "inline-block",
      width: "4px",
      height: "1.2em",
      backgroundColor: "#FF5336",
      marginRight: "0.5rem",
      verticalAlign: "middle",
      borderRadius: "2px",
    },
  },
  "& h3": {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: "#1a1f36",
    marginTop: "2rem",
    marginBottom: "0.75rem",
  },
  "& p": {
    fontSize: "1rem",
    lineHeight: 1.6,
    color: "#4a5568",
    marginBottom: "1.25rem",
  },
  "& code": {
    backgroundColor: "#FFF5F3",
    padding: "0.2em 0.4em",
    borderRadius: "4px",
    fontSize: "0.875em",
    color: "#FF5336",
    fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
  },
  "& pre": {
    backgroundColor: "#FFF5F3",
    padding: "1rem",
    borderRadius: "8px",
    overflow: "auto",
    marginBottom: "1.5rem",
    border: "1px solid #FFE5E0",
  },
  "& ul, & ol": {
    paddingLeft: "1.5rem",
    marginBottom: "1.25rem",
    "& li": {
      marginBottom: "0.5rem",
      color: "#4a5568",
      "&::marker": {
        color: "#FF5336",
      },
    },
  },
  "& a": {
    color: "#FF5336",
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
      color: "#FF7A36",
    },
  },
  "& blockquote": {
    borderLeft: "4px solid #FF5336",
    paddingLeft: "1rem",
    marginLeft: 0,
    marginBottom: "1.25rem",
    color: "#4a5568",
    fontStyle: "italic",
    backgroundColor: "#FFF5F3",
    padding: "1rem",
    borderRadius: "0 8px 8px 0",
  },
  "& table": {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: "1.5rem",
    "& th, & td": {
      padding: "0.75rem",
      border: "1px solid #FFE5E0",
    },
    "& th": {
      backgroundColor: "#FFF5F3",
      fontWeight: 600,
      color: "#1a1f36",
    },
    "& tr:nth-of-type(even)": {
      backgroundColor: "#FFF5F3",
    },
  },
}));

const BackButton = styled(Link)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  color: "#ffffff",
  textDecoration: "none",
  padding: "8px 16px",
  borderRadius: "8px",
  transition: "all 0.2s ease",
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    textDecoration: "none",
  },
}));

const Documentation = () => {
  const [open, setOpen] = useState({});
  const [selectedPath, setSelectedPath] = useState("test-studio/settings");
  const [content, setContent] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const theme = useTheme();
  
  const handleToggle = (path) => {
    setOpen((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  };

  const handleSelect = (path) => {
    setSelectedPath(path);
  };

  useEffect(() => {
    const loadContent = async () => {
      setIsLoading(true);
      try {
        const findFile = (items) => {
          for (const item of items) {
            if (item.path === selectedPath) return item.file;
            if (item.children) {
              const found = findFile(item.children);
              if (found) return found;
            }
          }
          return null;
        };

        const filePath = findFile(documentationStructure);
        if (filePath) {
          const response = await fetch(`/docs/${filePath}`);
          if (response.ok) {
            const text = await response.text();
            setContent(text);
          } else {
            setContent("# Content not found\n\nThe requested documentation page could not be loaded.");
          }
        } else {
          setContent("# Content not found\n\nThe requested documentation page does not exist.");
        }
      } catch (error) {
        console.error("Error loading documentation:", error);
        setContent("# Error loading content\n\nThere was an error loading the documentation. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, [selectedPath]);

  return (
    <DocumentationContainer>
      <StyledAppBar position="fixed">
        <Toolbar sx={{ 
          padding: "0 24px",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Logo 
              src={user?.company?.brandLogo || "/logo.png"} 
             
              alt="Nogrunt" 
            />
          </Box>
          <BackButton component={RouterLink} to="/new/dashboard">
            <ArrowBack sx={{ mr: 1, fontSize: "1.25rem" }} />
            Back to Dashboard
          </BackButton>
        </Toolbar>
      </StyledAppBar>
      
      <MainContent>
        <SidebarContainer>
          <Sidebar
            documentationStructure={documentationStructure}
            open={open}
            selectedPath={selectedPath}
            onToggle={handleToggle}
            onSelect={handleSelect}
          />
        </SidebarContainer>
        <Content>
          {isLoading ? (
            <Box 
              display="flex" 
              justifyContent="center" 
              alignItems="center" 
              height="100%"
              minHeight="200px"
            >
              <Typography sx={{ 
                color: "#4a5568",
                fontSize: "1.125rem",
              }}>
                Loading documentation...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ 
              minHeight: "100%",
              "& > *:first-of-type": {
                marginTop: 0,
              },
              "& > *:last-child": {
                marginBottom: 0,
              },
            }}>
              <MarkdownContent content={content} />
            </Box>
          )}
        </Content>
      </MainContent>
    </DocumentationContainer>
  );
};

export default Documentation; 